import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NoteService } from '../../services/note.service';
import { UserNote, NOTE_COLORS } from '../../models/note.model';
import { NotificationService } from '@core/services/notification.service';
import { AuthService } from '@features/auth/services/auth.service';

@Component({
  selector: 'app-floating-notes',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './floating-notes.component.html',
  styleUrls: ['./floating-notes.component.scss']
})
export class FloatingNotesComponent implements OnInit {
  private noteService = inject(NoteService);
  private notify = inject(NotificationService);
  private authService = inject(AuthService);

  isOpen = signal(false);
  notes = signal<UserNote[]>([]);
  newText = '';
  newColor = NOTE_COLORS[0].value;
  editingId = signal<number | null>(null);
  editText = '';
  readonly colors = NOTE_COLORS;

  get isEnabled(): boolean {
    return this.noteService.isWidgetEnabled();
  }

  ngOnInit(): void {
    if (this.isEnabled && this.authService.isAuthenticated()) {
      this.noteService.getNotes().subscribe({
        next: (notes) => this.notes.set(notes),
        error: () => {}
      });
    }
  }

  toggle(): void {
    this.isOpen.update(v => !v);
  }

  addNote(): void {
    if (!this.newText.trim()) return;
    this.noteService.createNote(this.newText.trim(), this.newColor).subscribe({
      next: (note) => {
        this.notes.update(n => [note, ...n]);
        this.newText = '';
      },
      error: () => this.notify.error('notes.saveError')
    });
  }

  startEdit(note: UserNote): void {
    this.editingId.set(note.id);
    this.editText = note.text;
  }

  saveEdit(note: UserNote): void {
    if (!this.editText.trim()) return;
    this.noteService.updateNote(note.id, { text: this.editText.trim(), color: note.color, isPinned: note.isPinned }).subscribe({
      next: (updated) => {
        this.notes.update(n => n.map(x => x.id === updated.id ? updated : x));
        this.editingId.set(null);
      }
    });
  }

  cancelEdit(): void {
    this.editingId.set(null);
  }

  togglePin(note: UserNote): void {
    this.noteService.updateNote(note.id, { text: note.text, color: note.color, isPinned: !note.isPinned }).subscribe({
      next: (updated) => {
        const sorted = this.notes().map(x => x.id === updated.id ? updated : x)
          .sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0));
        this.notes.set(sorted);
      }
    });
  }

  changeColor(note: UserNote, color: string): void {
    this.noteService.updateNote(note.id, { text: note.text, color, isPinned: note.isPinned }).subscribe({
      next: (updated) => this.notes.update(n => n.map(x => x.id === updated.id ? updated : x))
    });
  }

  onEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    this.addNote();
  }

  deleteNote(id: number): void {
    this.noteService.deleteNote(id).subscribe({
      next: () => this.notes.update(n => n.filter(x => x.id !== id)),
      error: () => this.notify.error('notes.deleteError')
    });
  }
}
